import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  MapPin,
  CircleCheck,
  Loader2,
  Check,
  Camera,
  RefreshCw,
  Phone,
  ChevronRight,
} from "lucide-react";
import { decryptData } from "../utils/storageCrypto";
import GoogleRoutePreview from "./components/GoogleRoutePreview";
import GeofenceBottomSheetModal from "./components/GeofenceBottomSheetModal";

/* -------------------- PHASE DEFINITIONS -------------------- */
const PHASES = {
  NAVIGATE: "navigate",         // Screen 1: Navigate to Drop-off[cite: 3]
  TAKE_PHOTO: "take_photo",     // Screen 3: Capture Delivery Photo[cite: 3]
  ENTER_OTP: "enter_otp",       // Screen 4: Drop-off OTP Confirmation[cite: 3]
  COMPLETED: "completed",       // Screen 5: Drop-off Completed[cite: 3]
};

const ActiveDropoffStep = ({
  step,
  totalStepsCount,
  onComplete,
  activeRide,
  isLastStep,
}) => {
  const stopStorageKey = `dropoff_phase_${activeRide?.tripId || "active"}_${step?.stop_id || step?.step || 1}`;

  const [phase, setPhase] = useState(() => {
    const saved = sessionStorage.getItem(stopStorageKey);
    return saved || PHASES.NAVIGATE;
  });

  const [otpValue, setOtpValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showGeofenceModal, setShowGeofenceModal] = useState(false);

  // Photo Capture State
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoTimestamp, setPhotoTimestamp] = useState(null);
  const fileInputRef = useRef(null);
  console.log(step);

  /* Calculate milestone label (D1, D2...) */
  const stepMilestoneLabel = useMemo(() => {
    if (!activeRide?.steps?.length) return `D${step?.step || 1}`;
    let count = 0;
    for (const s of activeRide.steps) {
      if (s.type === "DROPOFF") count += 1;
      if (s.step === step?.step || s.dg_id === step?.dg_id || s.stop_id === step?.stop_id) break;
    }
    return `D${count || 1}`;
  }, [activeRide, step]);

  /* Calculate stop index out of total (e.g. 3 OF 4) */
  const currentStopIndex = useMemo(() => {
    if (!activeRide?.steps?.length) return 1;
    const idx = activeRide.steps.findIndex(
      (s) => s.stop_id === step?.stop_id || s.step === step?.step
    );
    return idx >= 0 ? idx + 1 : 1;
  }, [activeRide, step]);

  /* Next step calculation for Screen 5 preview */
  const nextStepInfo = useMemo(() => {
    if (!activeRide?.steps?.length) return null;
    const curIdx = activeRide.steps.findIndex(
      (s) => s.stop_id === step?.stop_id || s.step === step?.step
    );
    if (curIdx >= 0 && curIdx < activeRide.steps.length - 1) {
      const nextS = activeRide.steps[curIdx + 1];
      const isPick = nextS.type === "PICKUP";
      return {
        label: isPick ? "Pickup" : "Drop-off",
        title: `${isPick ? "Pickup" : "Drop-off"} - ${nextS.name || nextS.area || "Next Stop"}`,
        distance: nextS.distance || "12.6 km",
        time: "22 min",
      };
    }
    return null;
  }, [activeRide, step]);

  useEffect(() => {
    if (!step) return;
    const currentStatus = step.status ? String(step.status).toUpperCase() : "";

    if (currentStatus === "COMPLETED" || currentStatus === "SKIPPED") {
      setPhase(PHASES.COMPLETED);
    } else if (currentStatus === "OTP_GENERATED") {
      setPhase(PHASES.ENTER_OTP);
    }
  }, [step]);

  const updatePhase = (newPhase) => {
    sessionStorage.setItem(stopStorageKey, newPhase);
    setPhase(newPhase);
  };

  /* Screen 1 -> Reached Drop-off Point Trigger */
  const handleReachedClick = () => {
    setShowGeofenceModal(true);
  };

  /* Screen 2 -> Continue to Delivery Photo */
  const handleProceedToPhoto = () => {
    setShowGeofenceModal(false);
    updatePhase(PHASES.TAKE_PHOTO);
  };

  /* Camera & File Upload Handling */
  const handlePhotoCapture = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCapturedPhoto(reader.result);
        const now = new Date();
        const formattedDate = now.toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });
        const formattedTime = now.toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
        });
        setPhotoTimestamp(`${formattedDate}, ${formattedTime}`);
      };
      reader.readAsDataURL(file);
    }
  };

  /* Screen 3 -> Upload Photo First -> If Success, Generate Drop-off OTP */
  const handleProceedToOTP = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const URL = process.env.REACT_APP_FORE_BRICKBUCKET;
      const encryptedToken = sessionStorage.getItem("token");
      const token = decryptData(encryptedToken);
      const stopId = step?.stop_id;
      console.log(stopId);
      const tripId = activeRide?.tripId;

      if (!stopId) {
        throw new Error("Stop ID not found for this delivery step.");
      }

      // ========================================================
      // 1. UPLOAD DELIVERED IMAGES API (Using stop_id query param)
      // ========================================================
      const formData = new FormData();
      if (photoFile) {
        formData.append("images", photoFile);
      } else if (capturedPhoto) {
        const blob = await (await fetch(capturedPhoto)).blob();
        formData.append("images", blob, "delivery_proof.jpg");
      } else {
        throw new Error("Please take a delivery photo first.");
      }

      const uploadRes = await fetch(
        `${URL}delivery/upload-delivered-images?stop_id=${stopId}`,
        {
          method: "POST",
          headers: {
            "x-token": token,
          },
          body: formData,
        }
      );

      const uploadData = await uploadRes.json();
      if (!uploadRes.ok || !uploadData.success) {
        throw new Error(uploadData.message || "Failed to upload delivery photo.");
      }

      // ========================================================
      // 2. GENERATE STOP OTP API
      // ========================================================
      const otpRes = await fetch(`${URL}delivery/generate-stop-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-token": token,
        },
        body: JSON.stringify({
          leg_id: step.leg_id,
          stop_id: stopId,
          trip_id: tripId,
        }),
      });

      const otpData = await otpRes.json();
      if (!otpRes.ok || !otpData.success) {
        throw new Error(otpData.message || "Failed to generate OTP.");
      }

      updatePhase(PHASES.ENTER_OTP);
    } catch (err) {
      console.error("Upload & OTP generation error:", err.message);
      setErrorMessage(err.message || "Failed to complete step. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* Screen 4 -> Verify OTP & Complete Drop-off */
  const handleVerifyOTP = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const URL = process.env.REACT_APP_FORE_BRICKBUCKET;
      const encryptedToken = sessionStorage.getItem("token");
      const token = decryptData(encryptedToken);
      const shipmentId = activeRide?.tripId || activeRide?._id || activeRide?.shipmentId;

      const res = await fetch(`${URL}delivery/verify-stop-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-token": token,
        },
        body: JSON.stringify({
          trip_id: shipmentId,
          stop_id: step.stop_id,
          otp: Number(otpValue),
          leg_id: step.leg_id,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || "OTP verification failed");

      sessionStorage.removeItem(stopStorageKey);
      updatePhase(PHASES.COMPLETED);
    } catch (err) {
      setErrorMessage(err.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3.5 pb-4">
      {/* ========================================================================= */}
      {/* SCREEN 1: NAVIGATE TO DROP-OFF                                           */}
      {/* ========================================================================= */}
      {phase === PHASES.NAVIGATE && (
        <div className="space-y-3 animate-fadeIn">
          {/* Destination Details Card */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-xs">
            <div className="bg-gray-50 border-b border-gray-100 p-3.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-black uppercase tracking-wider text-red-600 font-display">
                  CURRENT STOP • {currentStopIndex} OF {totalStepsCount}
                </span>
                <span className="text-[10px] font-bold text-amber-700 bg-amber-100/70 px-2.5 py-0.5 rounded-full border border-amber-200 font-mono">
                  Trip in progress
                </span>
              </div>
              <h2
                className="text-xl font-black text-gray-900 leading-tight mt-1"
                style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
              >
                {step?.name || `Drop-off ${stepMilestoneLabel}`}
              </h2>
            </div>

            <div className="p-3.5 space-y-2.5">
              <div className="flex items-start gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" />
                <p className="text-xs text-gray-600 leading-snug" style={{ fontFamily: "DMSans-Regular, sans-serif" }}>
                  {step?.address || "Address not specified"}
                </p>
              </div>
              <p className="text-[11px] font-mono font-bold text-gray-400 pl-5">
                Ref: {step?.refNumber || "OD-000208"}
              </p>

              <div className="flex items-center gap-4 pt-2 border-t border-gray-100 text-xs font-mono font-bold text-gray-600">
                <span>📍 {step?.distance || "4.8 km"}</span>
                <span>⏱️ {step?.estTime || "14 min (est.)"}</span>
              </div>
            </div>
          </div>

          {/* Real Google Map Preview with Road Path */}
          <GoogleRoutePreview
            destinationLat={step?.latitude}
            destinationLng={step?.longitude}
            destinationAddress={step?.address}
            destinationName={step?.name || "Drop-off Point"}
          />

          {/* Primary Action Button */}
          <div className="pt-1">
            <button
              type="button"
              onClick={handleReachedClick}
              className="w-full h-14 bg-white hover:bg-gray-50 active:scale-[0.99] border-2 border-red-600 text-red-600 rounded-2xl flex items-center justify-center gap-2 text-base font-black uppercase tracking-wider transition shadow-sm cursor-pointer"
              style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
            >
              <span>📍</span>
              <span>Reached Drop-off Point</span>
            </button>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCREEN 2: GEOFENCE VALIDATION BOTTOM SHEET (PRODUCTION SECURE GPS)         */}
      {/* ========================================================================= */}
      <GeofenceBottomSheetModal
        isOpen={showGeofenceModal}
        onClose={() => setShowGeofenceModal(false)}
        onProceed={handleProceedToPhoto}
        destinationLat={step?.latitude}
        destinationLng={step?.longitude}
        isPickup={false}
        thresholdMeters={100}
      />

      {/* ========================================================================= */}
      {/* SCREEN 3: CAPTURE DELIVERY PHOTO                                         */}
      {/* ========================================================================= */}
      {phase === PHASES.TAKE_PHOTO && (
        <div className="space-y-3.5 animate-fadeIn">
          <div className="flex items-start gap-2.5 bg-white p-3.5 rounded-2xl border border-gray-200 shadow-xs">
            <div className="w-8 h-8 rounded-xl bg-red-50 text-red-600 flex items-center justify-center shrink-0 mt-0.5">
              <Camera className="w-4 h-4" />
            </div>
            <div>
              <h3
                className="text-sm font-black text-gray-900"
                style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
              >
                Take Delivery Photo
              </h3>
              <p className="text-xs text-gray-500" style={{ fontFamily: "DMSans-Regular, sans-serif" }}>
                Take a clear photo of the delivered items at the customer location.
              </p>
            </div>
          </div>

          <div className="relative w-full h-72 rounded-2xl overflow-hidden border-2 border-dashed border-gray-300 bg-gray-50 flex flex-col items-center justify-center shadow-xs">
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handlePhotoCapture}
            />

            {capturedPhoto ? (
              <div className="relative w-full h-full">
                <img
                  src={capturedPhoto}
                  alt="Delivery Proof"
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-3 right-3 bg-slate-900/80 hover:bg-black text-white px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 shadow transition cursor-pointer"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  <span>Retake Photo</span>
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center justify-center p-6 text-center space-y-2 cursor-pointer active:scale-95 transition"
              >
                <div className="w-14 h-14 rounded-full bg-red-100 text-red-600 flex items-center justify-center shadow-xs">
                  <Camera className="w-7 h-7" />
                </div>
                <span
                  className="text-sm font-black text-gray-900 uppercase tracking-wider"
                  style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
                >
                  Tap to Open Camera
                </span>
                <span className="text-xs text-gray-400">
                  Proof of delivery required
                </span>
              </button>
            )}
          </div>

          {photoTimestamp && (
            <div className="flex items-center justify-between p-3 rounded-xl bg-white border border-gray-200 text-xs">
              <div className="flex items-center gap-2">
                <CircleCheck className="w-4 h-4 text-emerald-600" />
                <div>
                  <p className="font-bold text-gray-900">Photo captured</p>
                  <p className="text-[10px] text-gray-400">{photoTimestamp}</p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </div>
          )}

          {errorMessage && (
            <div className="p-3 rounded-2xl border border-red-200 bg-red-50 text-red-600 text-xs font-medium text-center">
              {errorMessage}
            </div>
          )}

          <div className="pt-2">
            <button
              type="button"
              disabled={loading || !capturedPhoto}
              onClick={handleProceedToOTP}
              className="w-full h-14 bg-red-600 hover:bg-red-700 active:scale-[0.98] text-white font-black text-sm uppercase tracking-wider rounded-2xl transition shadow-md flex items-center justify-center gap-2 cursor-pointer disabled:opacity-40"
              style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-white" />
                  <span>Uploading & Generating OTP...</span>
                </>
              ) : (
                <span>Proceed to Drop-off OTP</span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCREEN 4: DROP-OFF OTP CONFIRMATION                                      */}
      {/* ========================================================================= */}
      {phase === PHASES.ENTER_OTP && (
        <div className="space-y-4 animate-fadeIn">
          <div className="bg-white rounded-3xl border border-gray-200 p-5 shadow-xs space-y-5">
            <div className="flex items-center justify-between pb-1">
              <div className="flex items-center gap-2">
                <img
                  src="/iconwithtext.png"
                  alt="Brickbucket"
                  className="h-6 w-auto object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </div>
              <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full font-mono">
                DROP-OFF
              </span>
            </div>

            <div className="bg-gray-50/80 border border-gray-200/80 rounded-2xl p-3.5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center shrink-0 shadow-2xs text-gray-700">
                <svg
                  className="w-5 h-5 text-gray-700"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                  <rect width="20" height="14" x="2" y="6" rx="2" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-gray-400 leading-tight">
                  Order Reference
                </p>
                <h4 className="text-base font-black text-gray-900 tracking-wide font-mono mt-0.5">
                  {step?.refNumber || "OD-000208"}
                </h4>
              </div>
            </div>

            <div className="space-y-1 text-left pt-1">
              <h2
                className="text-lg font-black text-[#E9001E] leading-tight"
                style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
              >
                Enter 4-digit Drop-off OTP
              </h2>
              <p
                className="text-xs text-gray-500 leading-snug"
                style={{ fontFamily: "DMSans-Regular, sans-serif" }}
              >
                Ask the customer or authorized person for the code.
              </p>
            </div>

            <div className="relative">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                autoFocus
                value={otpValue}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, "");
                  if (val.length <= 4) setOtpValue(val);
                }}
                className="absolute inset-0 w-full h-full opacity-0 z-20 cursor-pointer"
              />

              <div className="grid grid-cols-4 gap-3 py-1">
                {[0, 1, 2, 3].map((index) => {
                  const digit = otpValue[index] || "";
                  const isCurrent = otpValue.length === index;

                  return (
                    <div
                      key={index}
                      className={`h-16 rounded-2xl flex items-center justify-center text-2xl font-black font-mono transition-all duration-150 ${
                        digit
                          ? "bg-white border-2 border-slate-300 text-gray-900 shadow-xs"
                          : isCurrent
                          ? "bg-white border-2 border-[#E9001E] shadow-sm ring-2 ring-red-100"
                          : "bg-gray-50/60 border border-gray-200 text-transparent"
                      }`}
                    >
                      {digit}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="pt-1">
              <a
                href="tel:+918247895454"
                className="inline-flex items-center justify-between w-full text-xs font-bold text-gray-600 hover:text-gray-900 transition py-1"
                style={{ fontFamily: "DMSans-Medium, sans-serif" }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-red-50 flex items-center justify-center text-[#E9001E]">
                    <Phone className="w-3.5 h-3.5" />
                  </div>
                  <span>Can't get OTP? Contact Support</span>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </a>
            </div>
          </div>

          {errorMessage && (
            <div className="p-3 rounded-2xl border border-red-200 bg-red-50 text-red-600 text-xs font-medium text-center">
              {errorMessage}
            </div>
          )}

          <button
            type="button"
            disabled={otpValue.length !== 4 || loading}
            onClick={handleVerifyOTP}
            className="w-full h-14 bg-[#E9001E] hover:bg-red-700 active:scale-[0.98] text-white font-black text-sm uppercase tracking-wider rounded-2xl transition shadow-md flex items-center justify-center gap-2 cursor-pointer disabled:opacity-40"
            style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin text-white" />
                <span>Verifying...</span>
              </>
            ) : (
              <span>Verify OTP & Complete Drop-off</span>
            )}
          </button>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCREEN 5: DROP-OFF COMPLETED CELEBRATION                                 */}
      {/* ========================================================================= */}
      {phase === PHASES.COMPLETED && (
        <div className="space-y-4 animate-fadeIn text-center py-2">
          <div className="w-20 h-20 rounded-full bg-emerald-500 text-white flex items-center justify-center mx-auto shadow-lg animate-scaleUp">
            <Check className="w-10 h-10 stroke-[3]" />
          </div>

          <div className="space-y-1">
            <h2
              className="text-2xl font-black text-gray-900"
              style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
            >
              Drop-off {stepMilestoneLabel} Completed!
            </h2>
            <p className="text-xs text-gray-500">
              Delivery confirmed with customer.
            </p>
          </div>

          {/* Static Informational Next Stop Card (Non-clickable, read-only) */}
          {nextStepInfo && (
            <div className="bg-white rounded-2xl border border-gray-200 p-3.5 shadow-xs flex items-center text-left select-none pointer-events-none">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-red-600 text-white flex items-center justify-center font-bold text-xs shrink-0">
                  {nextStepInfo.label === "Pickup" ? "P2" : "D2"}
                </div>
                <div>
                  <span className="text-[10px] font-black uppercase text-gray-400 tracking-wider">
                    Next Stop
                  </span>
                  <h4 className="text-xs font-black text-gray-900 leading-tight">
                    {nextStepInfo.title}
                  </h4>
                  <p className="text-[11px] font-mono text-gray-400 mt-0.5">
                    {nextStepInfo.distance} • {nextStepInfo.time}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Primary Action Button */}
          <div className="pt-2">
            <button
              type="button"
              onClick={() => {
                sessionStorage.removeItem(stopStorageKey);
                onComplete?.();
              }}
              className="w-full h-14 bg-red-600 hover:bg-red-700 active:scale-[0.98] text-white font-black text-sm uppercase tracking-wider rounded-2xl transition shadow-md cursor-pointer"
              style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
            >
              <span>{isLastStep ? "Complete Trip" : `Navigate to ${nextStepInfo?.label || "Next Stop"}`}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveDropoffStep;